import type { FC, ReactNode } from 'react'
import type { WeddingCardData } from '@/types'

import Link from 'next/link'
import { ArrowRight, MapPin } from 'lucide-react'
import { cn } from '@/lib/utils'
import { weddingField } from '@/lib/cms'
import { WeddingCover } from '@/components/generals/wedding-cover'

interface WeddingCardProps {
    wedding: WeddingCardData
    headingLevel?: 'h2' | 'h3'
    className?: string
}

export const WeddingCard: FC<WeddingCardProps> = ({ wedding, headingLevel = 'h3', className }): ReactNode => {
    const Heading = headingLevel
    const href = `/weddings/${wedding.slug}`

    return (
        <article className={cn('group flex flex-col', className)}>
            <Link
                href={href}
                tabIndex={-1}
                aria-hidden='true'
                className='arch-frame relative block aspect-[4/5] overflow-hidden bg-muted shadow-[0_24px_50px_-30px_rgba(74,13,24,0.55)]'
            >
                <WeddingCover
                    couple={wedding.couple}
                    image={wedding.coverImage}
                    imageAlt={wedding.coverImageAlt}
                    accentColor={wedding.accentColor}
                    className='transition-transform duration-700 ease-out group-hover:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover:scale-100'
                />
            </Link>

            <div className='flex flex-1 flex-col pt-6'>
                {wedding.weddingType && (
                    <p className='text-xs font-medium uppercase tracking-[0.24em] text-gold'>
                        <span data-mdlfy-cms={weddingField(wedding.id, 'wedding_type')}>{wedding.weddingType}</span>
                    </p>
                )}

                <Heading className='mt-2 text-3xl leading-tight text-foreground'>
                    <Link href={href} className='transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'>
                        <span data-mdlfy-cms={weddingField(wedding.id, 'couple')}>{wedding.couple}</span>
                    </Link>
                </Heading>

                {(wedding.location || wedding.weddingMonth) && (
                    <p className='mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground'>
                        <MapPin aria-hidden='true' className='size-4 text-gold' />

                        {wedding.location && (
                            <span data-mdlfy-cms={weddingField(wedding.id, 'location')}>{wedding.location}</span>
                        )}

                        {wedding.location && wedding.weddingMonth && <span aria-hidden='true'>·</span>}

                        {wedding.weddingMonth && (
                            <span data-mdlfy-cms={weddingField(wedding.id, 'wedding_month')}>{wedding.weddingMonth}</span>
                        )}
                    </p>
                )}

                {wedding.excerpt && (
                    <p data-mdlfy-cms={weddingField(wedding.id, 'excerpt')} className='mt-4 line-clamp-3 leading-relaxed text-foreground/80'>
                        {wedding.excerpt}
                    </p>
                )}

                <Link
                    href={href}
                    aria-label={`Read their story: ${wedding.couple}`}
                    className='mt-5 inline-flex items-center gap-2 self-start text-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                >
                    Read their story
                    <ArrowRight aria-hidden='true' className='size-4 transition-transform group-hover:translate-x-0.5' />
                </Link>
            </div>
        </article>
    )
}
